<script lang="ts">
	import { onMount, type Component } from "svelte"
	import { OverlayController, type OverlayOptions } from "./OverlayController"
	import { PlusIcon } from "luxedo-icons"
	import { Responsive } from "responsive"

	interface Props {
		id: any
		options: OverlayOptions<any>
		component: Component
	}

	let { id, options, component }: Props = $props()
	let blurAmount = $state(0)
	let isMobile = $derived(Responsive.isMobile)

	// The dialog box itself (NOT the backdrop) — it takes role="dialog", holds
	// focus while open, and is what Escape is scoped to.
	let dialogEl: HTMLDivElement | undefined = $state()
	// `id` is a uuid from OverlayController.openOverlay, so this is unique even
	// with several overlays stacked.
	const headingId = $derived(`overlay-heading-${String(id)}`)

	const closeOverlay = () => {
		OverlayController.closeOverlay(id)
	}

	onMount(() => {
		setTimeout(() => {
			blurAmount = 1
		})

		// Move focus into the dialog. Without this the overlay opens behind the
		// user's focus: a screen reader keeps reading the page underneath and
		// keyboard Tab walks the background content, so an auto-opened overlay
		// (the court-portal invite, What's New) was effectively invisible.
		//
		// Children mount before this parent, so skip the move when focus is
		// already inside — that means the overlay's own content autofocused
		// something more useful (a text field, say) and we must not steal it.
		const previouslyFocused = document.activeElement as HTMLElement | null
		if (dialogEl && !dialogEl.contains(document.activeElement)) dialogEl.focus()

		// Put focus back where the user left it when the overlay closes,
		// otherwise focus resets to <body> and keyboard users lose their place.
		return () => {
			if (previouslyFocused?.isConnected) previouslyFocused.focus()
		}
	})

	// Escape is bound to the dialog subtree rather than the window on purpose:
	// with several overlays stacked, only the one actually holding focus should
	// close, and that is always the topmost (we focus each on open). A window
	// listener would close all of them at once, and OverlayController exposes no
	// "is topmost" query to discriminate.
	const onDialogKeydown = (e: KeyboardEvent) => {
		if (e.key !== "Escape") return
		e.stopPropagation()
		closeOverlay()
	}

	const SvelteComponent_1 = $derived(component)
</script>

<!-- ON THE `options.zIndex!` IN BOTH style= ATTRIBUTES BELOW
     `zIndex` is optional on OverlayOptions because CALLERS may omit it, but by
     the time an overlay is rendered it always has one: openOverlay fills it in
     (`if (!opts.zIndex) opts.zIndex = ++currentIndex`) before the overlay
     enters the store, and Overlay.svelte — the only thing in the monorepo that
     renders this component — renders straight from that store.

     Asserted rather than defaulted on purpose. `options.zIndex ?? 0` would put
     a real `z-index: 99` on an overlay that today gets none at all: the current
     arithmetic yields NaN, `z-index: NaN` is an invalid declaration, and the
     browser drops it. A default would be a stacking-order change across the ~12
     apps on this package. A `!` erases at compile time, so the emitted
     component is byte-for-byte what it was. -->

<!-- The backdrop is a decorative scrim, so role="presentation" keeps it out of
     the a11y tree and outside the dialog boundary below. Its click-to-dismiss
     has no keyboard twin of its own by design — Escape and the close button are
     the keyboard paths, and duplicating dismissal onto the scrim would just add
     a Tab stop over dead space. -->
<div
	class="overlay-bg"
	role="presentation"
	onclick={closeOverlay}
	style="opacity: {blurAmount}; z-index: {(options.zIndex! + 1) * 100 - 1};"
>
	<!-- role="dialog" + aria-modal go on the BOX, not the backdrop: the backdrop
	     is the click-to-dismiss scrim and must stay outside the dialog's
	     accessible boundary. Named from the heading when there is one, so screen
	     readers announce "<heading> dialog" instead of an anonymous group.
	     tabindex="-1" makes the box programmatically focusable (see onMount)
	     without adding a Tab stop. -->
	<div
		bind:this={dialogEl}
		class:isMobile
		class="overlay {options.classOverlay ?? ''}"
		role="dialog"
		aria-modal="true"
		aria-labelledby={options.heading ? headingId : undefined}
		aria-label={options.heading ? undefined : "Dialog"}
		tabindex="-1"
		style="z-index: {(options.zIndex! + 1) * 100};"
		onclick={(e) => {
			e.stopImmediatePropagation()
			e.stopPropagation()
		}}
		onkeydown={onDialogKeydown}
	>
		{#if !options.hideExitButton}
			<button class="close-overlay-button icon" onclick={closeOverlay}>
				<PlusIcon />
			</button>
		{/if}
		{#if options.heading}
			<div id={headingId} class={`overlay-heading ${options.classHeading ?? ""}`}>
				{options.heading}
			</div>
		{/if}
		<SvelteComponent_1 {...{ overlayID: id, ...options.props }} {closeOverlay} />
	</div>
</div>

<style>
	:global(.overlay) {
		position: absolute;
		left: 0;
		right: 0;
		top: 0;
		bottom: 0;
		margin: auto;
		overflow: hidden;
		max-height: 80vh;
		max-width: 80vw;
	}

	/* The box is focused programmatically on open (tabindex="-1"), never by Tab.
	   Browsers reserve the default ring for :focus-visible so this is belt-and-
	   braces, but it guarantees no new outline appears around every overlay in
	   the ~12 apps sharing this component. The dialog announcement, not a ring,
	   is what tells the user focus moved. */
	.overlay:focus {
		outline: none;
	}

	.overlay .close-overlay-button {
		top: 0.25rem;
		right: 0.75rem;
	}

	.overlay-bg {
		/* backdrop-filter: blur(0.5rem); */
		width: 100vw;
		height: 100dvh;
		position: fixed;
		top: 0;
		right: 0;
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		transition: opacity 200ms;
	}

	.overlay-heading.align-left {
		padding-left: 1rem;
		text-align: left;
	}

	.overlay-heading {
		z-index: 15;
		width: 100%;
		padding: 0.25rem 3rem;
		overflow: hidden;
		white-space: nowrap;
		text-overflow: ellipsis;
		font-size: var(--text-h5);
		text-align: center;
		border-bottom: 2px solid var(--color-main);
		margin-bottom: 1rem;
	}

	.close-overlay-button {
		z-index: 20;
		position: absolute;
	}

	.close-overlay-button:hover,
	.close-overlay-button:focus-visible {
		color: var(--color-main);
	}

	.close-overlay-button :global(svg) {
		transform: rotate(45deg);
		width: 2rem;
	}

	.close-overlay-button :global(.svg-stroke) {
		stroke: var(--color-text);
	}

	.close-overlay-button:hover :global(.svg-stroke),
	.close-overlay-button:focus-visible :global(.svg-stroke) {
		stroke: var(--color-text-light);
	}

	.isMobile {
		width: fit-content;
		max-width: calc(95vw - 2rem);
		max-height: 95dvh;
		padding: 1rem;
	}

	div.overlay:global(.no-pad),
	div.overlay:global(.no-padding) {
		padding: 0;
	}

	.isMobile .close-overlay-button {
		top: 0.75rem;
	}

	.isMobile .close-overlay-button :global(svg) {
		width: 1rem;
		height: 1rem;
	}
</style>
